import { Box, Flex, Text } from '@chakra-ui/react';
import { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import Info from '@ui/components/Info';
import { InfoTooltip } from '@ui/components/InfoTooltip';
import { TokenSelectionDrawer } from '@ui/components/TokenSelectionDrawer';
import { useStrategyTokenUsdValue } from '@ui/hooks/useStrategyTokenUsdValue';
import { useTranslations } from '@ui/i18n';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { formatNumber, formatTokenAmount, parseValue } from '@ui/utils/format';
import type { LiquidityStrategyDepositToken } from '@ui/utils/strategy';
import {
  LiquidityStrategyTokenDict,
  useCoinReturnedForUsdValue,
  useDepositTokenBalances,
  useDepositTokenBalancesUsd,
  useDepositTokenBalanceUsd,
  useVaultDepositExchangeRate,
} from '@ui/utils/strategy';
import { useEffect, useRef } from 'react';

import { useStrategyStore } from '../useStrategyStore';

type StrategyTokenSelectionDrawerProps = {
  onClose: () => void;
  isOpen: boolean;
  value: LiquidityStrategyDepositToken;
  preferred?: LiquidityStrategyDepositToken;
  onChange: (token: LiquidityStrategyDepositToken) => void;
  onCheck?: (token: LiquidityStrategyDepositToken) => void;
};

export const StrategyTokenSelectionDrawer = ({
  onClose,
  isOpen,
  value,
  onChange,
  onCheck,
  preferred,
}: StrategyTokenSelectionDrawerProps) => {
  const tokenList: LiquidityStrategyDepositToken[] = [
    'cro',
    'wcro',
    'lcro',
    'ferroLP',
  ];
  const tokenBalanceDict = useDepositTokenBalances();
  const tokenUsdBalanceDict = useDepositTokenBalancesUsd();
  const tokenDict = LiquidityStrategyTokenDict;
  const closedBefore = useRef(true);

  // initally, call onCheck with current value (if oncheck is available)
  useEffect(() => {
    if (closedBefore.current && isOpen && onCheck) {
      onCheck(value);
      closedBefore.current = false;
    }
    if (!isOpen) {
      closedBefore.current = true;
    }
  }, [isOpen, onCheck, value]);

  return (
    <TokenSelectionDrawer
      onClose={onClose}
      isOpen={isOpen}
      value={value}
      onChange={(token) => onChange(token)}
      tokenList={tokenList}
      tokenBalanceDict={tokenBalanceDict}
      tokenUsdBalanceDict={tokenUsdBalanceDict}
      tokenDict={tokenDict}
      preferred={preferred}
      Footer={<StrategyTokenSelectionDrawerFooter />}
      onCheck={onCheck}
    />
  );
};

const StrategyTokenSelectionDrawerFooter = () => {
  const amountStr = useStrategyStore((s) => s.amountStr);
  const depositToken = useStrategyStore((s) => s.depositToken);
  const checkedToken = useStrategyStore((s) => s.checkedToken);
  const tokenInfo = LiquidityStrategyTokenDict[checkedToken];
  const t = useTranslations();

  const depositAmountUsd = useDepositTokenBalanceUsd({
    token: depositToken,
    amount: parseValue(
      amountStr,
      LiquidityStrategyTokenDict[depositToken].decimals,
    ),
  });

  const coinReturnedForUsd = useCoinReturnedForUsdValue({
    usdAmount: depositAmountUsd,
  });

  const exchangeRateDepositRet = useVaultDepositExchangeRate(
    checkedToken,
    coinReturnedForUsd[checkedToken] ?? BigNumber.from(0),
    { enabled: !!coinReturnedForUsd[checkedToken] },
  );
  const receivedAmountFromDeposit =
    bigNumberToNumber(coinReturnedForUsd[checkedToken] ?? BigNumber.from(0)) ===
    0
      ? '0.00'
      : exchangeRateDepositRet.data
      ? (
          bigNumberToNumber(
            coinReturnedForUsd[checkedToken] ?? BigNumber.from(0),
          ) / exchangeRateDepositRet.data
        ).toFixed(2)
      : null;

  const receivedAmountFromDepositUsd = useStrategyTokenUsdValue(
    receivedAmountFromDeposit
      ? parseUnits(receivedAmountFromDeposit)
      : undefined,
  );

  if (receivedAmountFromDepositUsd === 0) return null;

  return (
    <>
      <Info
        minH="49px"
        title={<Box textColor="text-light">{t('If you deposit')}</Box>}
        convertedValue={
          depositAmountUsd ? (
            <Box textColor="text-light">
              {formatTokenAmount(depositAmountUsd, 2)} USD
            </Box>
          ) : undefined
        }
        value={
          coinReturnedForUsd[checkedToken] ? (
            <Box textColor="text-light">
              {formatTokenAmount(
                coinReturnedForUsd[checkedToken],
                2,
                tokenInfo.decimals,
              )}{' '}
              {tokenInfo.name}
            </Box>
          ) : undefined
        }
      />
      <Info
        minH="49px"
        title={
          <>
            {t('You will receive')}
            <InfoTooltip
              title={tokenInfo.vaultTokenName}
              label={
                <Text px={{ base: 0, desktop: 8 }} py={{ base: 0, desktop: 4 }}>
                  {t('receive-less-token')}
                </Text>
              }
            />
          </>
        }
        convertedValueLoading={!receivedAmountFromDepositUsd && !!amountStr}
        convertedValue={
          receivedAmountFromDepositUsd
            ? formatNumber(receivedAmountFromDepositUsd, 2) + ' USD'
            : undefined
        }
        value={
          receivedAmountFromDeposit &&
          !Number.isNaN(receivedAmountFromDeposit) ? (
            <Flex align="center">
              {receivedAmountFromDeposit} {tokenInfo.vaultTokenName}{' '}
              <InfoTooltip
                title={tokenInfo.vaultTokenName}
                label={
                  <Text
                    px={{ base: 0, desktop: 8 }}
                    py={{ base: 0, desktop: 4 }}
                  >
                    {t('vlcro-explainer-{token}-{strategy}', {
                      token: tokenInfo.vaultTokenName,
                      strategy: t('Liquidity strategy'),
                    })}
                  </Text>
                }
              />
            </Flex>
          ) : amountStr ? undefined : (
            '--'
          )
        }
      />
    </>
  );
};
